import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { apiFetch } from './api';

const WorkflowEditor = ({ workflowId, onZurueck }) => {
  const [workflow, setWorkflow] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gespeichert, setGespeichert] = useState(false);
  const [neuTask, setNeuTask] = useState(false);
  const [taskName, setTaskName] = useState('');
  const [taskOffset, setTaskOffset] = useState(-1);

  const lade = async () => {
    const res = await apiFetch(`/workflows/${workflowId}`);
    const data = await res.json();
    setWorkflow(data);
    setTasks(data.tasks || []);
    setLoading(false);
  };

  useEffect(() => { lade(); }, [workflowId]);

  const workflowSpeichern = async (neuerName, neueBeschreibung) => {
    await apiFetch(`/workflows/${workflowId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: neuerName, beschreibung: neueBeschreibung })
    });
    setGespeichert(true);
    setTimeout(() => setGespeichert(false), 2000);
  };

  const taskHinzufuegen = async () => {
    if (!taskName) return;
    await apiFetch(`/workflows/${workflowId}/tasks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: taskName,
        prioritaet: 'mittel',
        deadline_offset: Number(taskOffset),
        reihenfolge: tasks.length
      })
    });
    setTaskName(''); setTaskOffset(-1); setNeuTask(false);
    lade();
  };

  const taskAktualisieren = async (task, feld, wert) => {
    const neuerWert = feld === 'deadline_offset' ? Number(wert) : wert;
    const aktualisiert = { ...task, [feld]: neuerWert };
    await apiFetch(`/workflows/${workflowId}/tasks/${task.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(aktualisiert)
    });
    // Lokalen State aktualisieren ohne Reload – Reihenfolge bleibt erhalten
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, [feld]: neuerWert } : t));
    setGespeichert(true);
    setTimeout(() => setGespeichert(false), 2000);
  };

  const taskLoeschen = async (taskId) => {
    await apiFetch(`/workflows/${workflowId}/tasks/${taskId}`, { method: 'DELETE' });
    lade();
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const neueTasks = Array.from(tasks);
    const [verschoben] = neueTasks.splice(result.source.index, 1);
    neueTasks.splice(result.destination.index, 0, verschoben);
    setTasks(neueTasks);
    for (let i = 0; i < neueTasks.length; i++) {
      await apiFetch(`/workflows/${workflowId}/tasks/${neueTasks[i].id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...neueTasks[i], reihenfolge: i })
      });
    }
  };

  const offsetLabel = (offset) => {
    const n = Number(offset);
    if (n === 0) return 'Am Projekttag';
    if (n === -1) return '1 Tag vorher';
    if (n < 0) return `${Math.abs(n)} Tage vorher`;
    if (n === 1) return '1 Tag danach';
    return `${n} Tage danach`;
  };

  if (loading) return <div style={{ padding: '40px', color: '#9b9b9b', fontSize: '14px' }}>Lädt...</div>;

  return (
    <div style={{ padding: '40px', position: 'relative' }}>
      {gespeichert && (
        <div style={{
          position: 'fixed', top: '24px', right: '24px', fontSize: '12px', color: '#6b6b6b',
          backgroundColor: '#ffffff', border: '1px solid #efefef', borderRadius: '8px',
          padding: '6px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', zIndex: 100
        }}>✓ Gespeichert</div>
      )}

      <div onClick={onZurueck} style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#9b9b9b',
        fontSize: '13px', cursor: 'pointer', marginBottom: '32px'
      }}>← Workflows</div>

      <input type="text" defaultValue={workflow.name}
        onBlur={(e) => workflowSpeichern(e.target.value, workflow.beschreibung)}
        style={{
          width: '100%', fontSize: '28px', fontWeight: '700', color: '#1a1a1a', border: 'none',
          outline: 'none', backgroundColor: 'transparent', marginBottom: '8px', letterSpacing: '-0.5px', boxSizing: 'border-box'
        }}
      />
      <input type="text" defaultValue={workflow.beschreibung}
        onBlur={(e) => workflowSpeichern(workflow.name, e.target.value)}
        placeholder="Beschreibung hinzufügen..."
        style={{
          width: '100%', fontSize: '14px', color: '#9b9b9b', border: 'none', outline: 'none',
          backgroundColor: 'transparent', marginBottom: '36px', boxSizing: 'border-box'
        }}
      />

      <div style={{
        backgroundColor: '#fafafa', border: '1px solid #e8e8e8', borderRadius: '8px',
        padding: '14px 16px', marginBottom: '28px', fontSize: '13px', color: '#6b6b6b',
        lineHeight: '1.6', maxWidth: '60%'
      }}>
        💡 Deadlines sind relativ zur Projektdeadline. <strong>-1</strong> = einen Tag vorher, <strong>-5</strong> = fünf Tage vorher. Beim Anwenden gibst du die Projektdeadline ein – alle Task-Deadlines werden automatisch berechnet.
      </div>

      <div style={{ marginBottom: '24px' }}>
        <p style={{ fontSize: '11px', fontWeight: '500', color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
          Tasks ({tasks.length}) — ziehen zum Sortieren
        </p>

        {tasks.length === 0 && !neuTask && (
          <p style={{ fontSize: '13px', color: '#c4c4c4', marginBottom: '16px' }}>Noch keine Tasks. Füge die ersten Schritte hinzu.</p>
        )}

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="workflow-tasks">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                {tasks.map((t, i) => (
                  <Draggable key={String(t.id)} draggableId={String(t.id)} index={i}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          backgroundColor: snapshot.isDragging ? '#f7f7f5' : '#ffffff',
                          border: '1px solid ' + (snapshot.isDragging ? '#d4d4d4' : '#efefef'),
                          borderRadius: '8px', padding: '12px 16px',
                          display: 'flex', alignItems: 'center', gap: '12px',
                          boxShadow: snapshot.isDragging ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                          ...provided.draggableProps.style
                        }}
                      >
                        <div {...provided.dragHandleProps} style={{
                          color: '#c4c4c4', cursor: 'grab', fontSize: '16px', flexShrink: 0,
                          display: 'flex', flexDirection: 'column', gap: '2px', padding: '2px'
                        }}>
                          <div style={{ display: 'flex', gap: '3px' }}>
                            <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#c4c4c4' }} />
                            <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#c4c4c4' }} />
                          </div>
                          <div style={{ display: 'flex', gap: '3px' }}>
                            <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#c4c4c4' }} />
                            <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#c4c4c4' }} />
                          </div>
                          <div style={{ display: 'flex', gap: '3px' }}>
                            <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#c4c4c4' }} />
                            <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#c4c4c4' }} />
                          </div>
                        </div>

                        <span style={{ fontSize: '12px', color: '#c4c4c4', flexShrink: 0, minWidth: '20px' }}>{i + 1}.</span>

                        <input type="text" defaultValue={t.name}
                          onBlur={(e) => taskAktualisieren(t, 'name', e.target.value)}
                          style={{
                            flex: 1, fontSize: '14px', color: '#1a1a1a', border: 'none',
                            outline: 'none', backgroundColor: 'transparent', minWidth: 0
                          }}
                        />

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                          <input type="number" defaultValue={t.deadline_offset}
                            onBlur={(e) => taskAktualisieren(t, 'deadline_offset', e.target.value)}
                            style={{
                              width: '60px', padding: '6px 8px', fontSize: '13px',
                              border: '1px solid #e8e8e8', borderRadius: '6px',
                              outline: 'none', textAlign: 'center'
                            }}
                          />
                          <span style={{ fontSize: '12px', color: '#9b9b9b', whiteSpace: 'nowrap', minWidth: '100px' }}>
                            {offsetLabel(t.deadline_offset)}
                          </span>
                        </div>

                        <button onClick={() => taskLoeschen(t.id)} style={{
                          width: '24px', height: '24px', borderRadius: '6px', border: 'none',
                          backgroundColor: 'transparent', color: '#c4c4c4', cursor: 'pointer',
                          fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>×</button>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {neuTask ? (
          <div style={{ backgroundColor: '#ffffff', border: '1px solid #e8e8e8', borderRadius: '8px', padding: '14px 16px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
              <input type="text" value={taskName} autoFocus
                onChange={(e) => setTaskName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') taskHinzufuegen(); if (e.key === 'Escape') setNeuTask(false); }}
                placeholder="Task-Name..."
                style={{
                  flex: 1, padding: '8px 12px', fontSize: '14px', border: '1px solid #e8e8e8',
                  borderRadius: '6px', outline: 'none'
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <label style={{ fontSize: '12px', color: '#6b6b6b' }}>Tage vor Projektdeadline:</label>
              <input type="number" value={Math.abs(taskOffset)}
                onChange={(e) => setTaskOffset(-Math.abs(Number(e.target.value)))}
                min="0"
                style={{
                  width: '70px', padding: '7px 8px', fontSize: '13px', border: '1px solid #e8e8e8',
                  borderRadius: '6px', outline: 'none', textAlign: 'center'
                }}
              />
              <span style={{ fontSize: '12px', color: '#9b9b9b' }}>{offsetLabel(taskOffset)}</span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={taskHinzufuegen} style={{
                padding: '7px 16px', backgroundColor: '#1a1a1a', color: '#fff', border: 'none',
                borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
              }}>Hinzufügen</button>
              <button onClick={() => { setNeuTask(false); setTaskName(''); setTaskOffset(-1); }} style={{
                padding: '7px 16px', backgroundColor: 'transparent', color: '#6b6b6b',
                border: '1px solid #e8e8e8', borderRadius: '6px', fontSize: '13px', cursor: 'pointer'
              }}>Abbrechen</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setNeuTask(true)} style={{
            width: '100%', padding: '10px', backgroundColor: 'transparent', color: '#6b6b6b',
            border: '1px dashed #d4d4d4', borderRadius: '8px', fontSize: '13px', cursor: 'pointer'
          }}>+ Task hinzufügen</button>
        )}
      </div>

      {tasks.length > 0 && (
        <div style={{
          backgroundColor: '#fafafa', border: '1px solid #e8e8e8',
          borderRadius: '8px', padding: '16px 20px', maxWidth: '60%'
        }}>
          <p style={{ fontSize: '11px', fontWeight: '500', color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
            Vorschau bei Projektdeadline 15.6.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {tasks.map(t => {
              const beispiel = new Date('2026-06-15');
              beispiel.setDate(beispiel.getDate() + Number(t.deadline_offset));
              return (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
                  <span style={{ color: '#1a1a1a', flex: 1 }}>{t.name}</span>
                  <span style={{ color: '#6b6b6b', minWidth: '80px', textAlign: 'right' }}>
                    {beispiel.getDate()}.{beispiel.getMonth() + 1}.{beispiel.getFullYear()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowEditor;